import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { getCurrencySymbol } from '../../../../../utils/currency-symbol.util';
import { SubscriptionService } from '../../../../../services/subscription.service';
import { FNFResponse } from '../../../../../models/response';
import { PriceLabel } from '../../../../../utils/plan-pricing.util';

export interface ChangePlanConfirmationData {
  planId: string;
  direction: 'upgrade' | 'downgrade';
  fromPlanName: string;
  fromPrice: number;
  toPlanName: string;
  toPrice: number;
  /** Per-order aware price text; falls back to `fromPrice`/`toPrice` per month. */
  fromPriceLabel?: PriceLabel;
  toPriceLabel?: PriceLabel;
  /** Flat paid upgrade: prorated and charged now. */
  prorated?: boolean;
  lostFeatures?: string[];
  /** When a downgrade applies (period end), formatted in the restaurant's timezone. */
  effectiveLabel?: string;
}

@Component({
  selector: 'app-change-plan-confirmation',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './change-plan-confirmation.component.html',
  styleUrl: './change-plan-confirmation.component.css',
})
export class ChangePlanConfirmationComponent {
  readonly dialogRef = inject(MatDialogRef<ChangePlanConfirmationComponent>);
  readonly data = inject<ChangePlanConfirmationData>(MAT_DIALOG_DATA);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly toastr = inject(ToastrService);
  protected readonly getCurrencySymbol = getCurrencySymbol;
  currency = 'USD';
  loading = false;

  get isUpgrade() {
    return this.data.direction === 'upgrade';
  }

  /** "on Nov 28, 2026" or "at the end of your billing period". */
  get effectiveText(): string {
    return this.data.effectiveLabel
      ? `on ${this.data.effectiveLabel}`
      : 'at the end of your billing period';
  }

  /** "until Nov 28, 2026" or "until the end of your billing period". */
  get untilText(): string {
    return this.data.effectiveLabel
      ? `until ${this.data.effectiveLabel}`
      : 'until the end of your billing period';
  }

  get fromLabel(): PriceLabel {
    return (
      this.data.fromPriceLabel ?? {
        amount: `${getCurrencySymbol(this.currency)}${this.data.fromPrice.toFixed(2)}`,
        suffix: '/mon',
      }
    );
  }

  get toLabel(): PriceLabel {
    return (
      this.data.toPriceLabel ?? {
        amount: `${getCurrencySymbol(this.currency)}${this.data.toPrice.toFixed(2)}`,
        suffix: '/mon',
      }
    );
  }

  cancel() {
    if (this.loading) {
      return;
    }
    this.dialogRef.close(false);
  }

  confirm() {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.subscriptionService.updateSubscriptionPlan({ planId: this.data.planId }).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.loading = false;
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          this.loading = false;
          this.toastr.error('No data found!', 'Error');
          return;
        }
        if (this.isUpgrade) {
          this.toastr.success(
            `Your plan has been changed to ${this.data.toPlanName}.`,
            'Plan Updated',
          );
        } else {
          this.toastr.success(
            `You'll move to ${this.data.toPlanName} ${this.effectiveText}. Until then nothing changes.`,
            'Plan Change Scheduled',
          );
        }
        this.dialogRef.close(true);
      },
      error: () => {
        this.loading = false;
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }
}
